import React, { useState, useEffect } from 'react';
import { cashbookApi, vehicleApi, formatApiError } from '../services/api';
import {
  Vault, Plus, PencilSimple, Trash, FileArrowDown, ArrowUp, ArrowDown,
  Calendar, Calculator, Car, Buildings, X,
} from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

const MONTH_NAMES = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];

const todayISO = () => new Date().toISOString().split('T')[0];

const emptyForm = {
  entry_date: todayISO(),
  category: 'Miete',
  description: '',
  amount: '',
  direction: 'in',
  document_ref: '',
  notes: '',
  supplier: '',
  vehicle_id: '',
};

const Cashbook = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState({ entries: [], starting_balance: 0, ending_balance: 0, total_in: 0, total_out: 0, count: 0 });
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  // Filters
  const [filterVehicleId, setFilterVehicleId] = useState('all');
  const [filterSupplier, setFilterSupplier] = useState('all');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  // Tagesabschluss / Kassensturz modal
  const [showClosingModal, setShowClosingModal] = useState(false);
  const [closingDate, setClosingDate] = useState(todayISO());
  const [closingData, setClosingData] = useState(null);
  const [closingCounted, setClosingCounted] = useState('');
  const [closingNotes, setClosingNotes] = useState('');
  const [closingLoading, setClosingLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => { loadAll(); }, [year, month, filterVehicleId, filterSupplier]);

  const loadAll = async () => {
    try {
      const listParams = { year, month };
      if (filterVehicleId && filterVehicleId !== 'all') listParams.vehicle_id = filterVehicleId;
      if (filterSupplier && filterSupplier !== 'all') listParams.supplier = filterSupplier;
      const [d, cats, sum, sups, vhs] = await Promise.all([
        cashbookApi.list(listParams),
        cashbookApi.categories(),
        cashbookApi.summary(),
        cashbookApi.suppliers(),
        vehicleApi.getAll(),
      ]);
      setData(d.data);
      setCategories(cats.data);
      setSummary(sum.data);
      setSuppliers(sups.data || []);
      setVehicles(vhs.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditing(null);
    setFormData({ ...emptyForm });
    setShowModal(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setFormData({
      entry_date: e.entry_date ? e.entry_date.substring(0, 10) : todayISO(),
      category: e.category || 'Miete',
      description: e.description || '',
      amount: e.amount != null ? String(e.amount) : '',
      direction: e.direction || 'in',
      document_ref: e.document_ref || '',
      notes: e.notes || '',
      supplier: e.supplier || '',
      vehicle_id: e.vehicle_id || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    try {
      const payload = {
        entry_date: new Date(formData.entry_date).toISOString(),
        category: formData.category,
        description: formData.description,
        amount: parseFloat(formData.amount),
        direction: formData.direction,
        document_ref: formData.document_ref || null,
        notes: formData.notes || null,
        supplier: formData.supplier ? formData.supplier.trim() : null,
        vehicle_id: formData.vehicle_id || null,
      };
      if (editing) {
        await cashbookApi.update(editing.id, payload);
        toast.success('Eintrag aktualisiert');
      } else {
        await cashbookApi.create(payload);
        toast.success('Eintrag erfasst');
      }
      setShowModal(false);
      setEditing(null);
      setFormData(emptyForm);
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kassenbuch-Eintrag wirklich löschen?')) return;
    try {
      await cashbookApi.delete(id);
      toast.success('Eintrag gelöscht');
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const downloadBlob = async (apiFn, filename) => {
    try {
      const res = await apiFn();
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const exportPdf = () => downloadBlob(() => cashbookApi.exportPdf(year, month), `kassenbuch_${year}_${String(month).padStart(2, '0')}.pdf`);
  const exportCsv = () => downloadBlob(() => cashbookApi.exportCsv(year, month), `kassenbuch_${year}_${String(month).padStart(2, '0')}.csv`);

  const openClosingModal = async () => {
    setClosingDate(todayISO());
    setClosingCounted('');
    setClosingNotes('');
    setClosingData(null);
    setShowClosingModal(true);
  };

  const loadClosing = async (date) => {
    setClosingLoading(true);
    try {
      const res = await cashbookApi.dailyClosing(date);
      setClosingData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setClosingLoading(false);
    }
  };

  useEffect(() => {
    if (showClosingModal && closingDate) {
      loadClosing(closingDate);
    }
  }, [showClosingModal, closingDate]);

  const downloadClosingPdf = () => {
    const counted = closingCounted ? Number(closingCounted) : undefined;
    return downloadBlob(
      () => cashbookApi.dailyClosingPdf(closingDate, counted, closingNotes || undefined),
      `kassensturz_${closingDate}.pdf`,
    );
  };

  const closingDiff = closingData && closingCounted
    ? Number(closingCounted) - Number(closingData.expected_cash)
    : null;

  const fmt = (v) => `€ ${Number(v || 0).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtDate = (s) => s ? new Date(s).toLocaleDateString('de-DE') : '—';

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);

  return (
    <div data-testid="cashbook-page">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
        <div>
          <p className="overline text-gray-500 mb-2">Finanzen</p>
          <h1 className="h1 text-[#1a1a1a]">Kassenbuch</h1>
          <p className="body text-gray-600 mt-1">Bareinnahmen und -ausgaben chronologisch mit laufendem Saldo</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openClosingModal} data-testid="open-closing-btn">
            <Calculator size={18} className="mr-2" />
            Kassensturz
          </Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
            <FileArrowDown size={18} className="mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={exportPdf} data-testid="export-pdf-btn">
            <FileArrowDown size={18} className="mr-2" />
            PDF-Auszug
          </Button>
          <Button className="btn-primary" onClick={openNew} data-testid="add-cashbook-btn">
            <Plus size={18} className="mr-2" />
            Neuer Eintrag
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="card-swiss p-4">
          <div className="flex items-center gap-3">
            <Vault size={28} weight="duotone" className="text-[#1a1a1a]" />
            <div>
              <p className="text-xs text-gray-500">Aktueller Kassenstand</p>
              <p className="text-2xl font-bold">{summary ? fmt(summary.balance) : '…'}</p>
            </div>
          </div>
        </div>
        <div className="card-swiss p-4">
          <div className="flex items-center gap-3">
            <ArrowDown size={28} weight="duotone" className="text-green-600" />
            <div>
              <p className="text-xs text-gray-500">Einnahmen {MONTH_NAMES[month - 1]}</p>
              <p className="text-2xl font-bold text-green-700">{fmt(data.total_in)}</p>
            </div>
          </div>
        </div>
        <div className="card-swiss p-4">
          <div className="flex items-center gap-3">
            <ArrowUp size={28} weight="duotone" className="text-[#E60000]" />
            <div>
              <p className="text-xs text-gray-500">Ausgaben {MONTH_NAMES[month - 1]}</p>
              <p className="text-2xl font-bold text-[#E60000]">{fmt(data.total_out)}</p>
            </div>
          </div>
        </div>
        <div className="card-swiss p-4">
          <div className="flex items-center gap-3">
            <Calendar size={28} weight="duotone" className="text-[#525866]" />
            <div>
              <p className="text-xs text-gray-500">Anfangsbestand</p>
              <p className="text-2xl font-bold">{fmt(data.starting_balance)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Month selector + Filters */}
      <div className="flex gap-2 items-center mb-4 flex-wrap">
        <Label className="overline mr-2">Zeitraum:</Label>
        <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
          <SelectTrigger className="input-swiss w-[150px]" data-testid="cashbook-month-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTH_NAMES.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
          <SelectTrigger className="input-swiss w-[100px]" data-testid="cashbook-year-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>

        <div className="w-px h-6 bg-[#E2E4E9] mx-2" />
        <Label className="overline mr-1"><Car size={14} className="inline mr-1" />Fahrzeug:</Label>
        <Select value={filterVehicleId} onValueChange={setFilterVehicleId}>
          <SelectTrigger className="input-swiss w-[220px]" data-testid="cashbook-vehicle-filter">
            <SelectValue placeholder="Alle" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Alle Fahrzeuge</SelectItem>
            {vehicles.map(v => (
              <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.license_plate})</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Label className="overline mr-1"><Buildings size={14} className="inline mr-1" />Lieferant:</Label>
        <Select value={filterSupplier} onValueChange={setFilterSupplier}>
          <SelectTrigger className="input-swiss w-[200px]" data-testid="cashbook-supplier-filter">
            <SelectValue placeholder="Alle" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value="all">Alle Lieferanten</SelectItem>
            {suppliers.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>

        {(filterVehicleId !== 'all' || filterSupplier !== 'all') && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => { setFilterVehicleId('all'); setFilterSupplier('all'); }}
            data-testid="cashbook-clear-filters"
          >
            <X size={14} className="mr-1" /> Filter zurücksetzen
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Lade Kassenbuch…</div>
      ) : data.entries.length === 0 ? (
        <div className="card-swiss p-12 text-center">
          <Vault size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="h3 mb-2">Keine Einträge im {MONTH_NAMES[month - 1]} {year}</h3>
          <p className="body text-gray-600 mb-6">Bar-Zahlungen auf Rechnungen werden automatisch erfasst. Weitere Bareinnahmen/-ausgaben können Sie hier direkt buchen.</p>
          <Button className="btn-primary" onClick={openNew}>
            <Plus size={18} className="mr-2" />
            Ersten Eintrag buchen
          </Button>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full card-swiss">
            <thead>
              <tr className="border-b border-[#E2E4E9] bg-[#F7F8FA]">
                <th className="text-left p-3 overline text-gray-600">Datum</th>
                <th className="text-left p-3 overline text-gray-600">Beleg</th>
                <th className="text-left p-3 overline text-gray-600">Kategorie</th>
                <th className="text-left p-3 overline text-gray-600">Beschreibung</th>
                <th className="text-right p-3 overline text-gray-600">Einnahme</th>
                <th className="text-right p-3 overline text-gray-600">Ausgabe</th>
                <th className="text-right p-3 overline text-gray-600">Saldo</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-[#F7F8FA] border-b border-[#E2E4E9]">
                <td colSpan={6} className="p-3 font-semibold text-sm text-gray-600 text-right">Anfangsbestand:</td>
                <td className="p-3 text-right font-semibold">{fmt(data.starting_balance)}</td>
                <td></td>
              </tr>
              {data.entries.map(e => (
                <tr key={e.id} className="border-b border-[#E2E4E9] hover:bg-[#F7F8FA]" data-testid={`cashbook-row-${e.id}`}>
                  <td className="p-3 text-sm">{fmtDate(e.entry_date)}</td>
                  <td className="p-3 text-sm font-mono text-xs">{e.document_ref || '—'}</td>
                  <td className="p-3 text-sm">
                    <span className="px-2 py-0.5 rounded-sm text-xs bg-gray-100 text-gray-700">{e.category}</span>
                    {e.auto_posted && <span className="ml-1 text-[10px] text-green-600">auto</span>}
                  </td>
                  <td className="p-3 text-sm">
                    {e.description}
                    {(e.supplier || e.vehicle_label) && (
                      <div className="text-[11px] text-[#8A94A6] mt-0.5 flex flex-wrap gap-x-2 gap-y-0.5">
                        {e.supplier && <span data-testid={`cashbook-supplier-${e.id}`}><Buildings size={11} className="inline mr-0.5" />{e.supplier}</span>}
                        {e.vehicle_label && <span data-testid={`cashbook-vehicle-${e.id}`}><Car size={11} className="inline mr-0.5" />{e.vehicle_label}</span>}
                      </div>
                    )}
                  </td>
                  <td className="p-3 text-right text-sm">
                    {e.direction === 'in' ? <span className="font-semibold text-green-700">{fmt(e.amount)}</span> : ''}
                  </td>
                  <td className="p-3 text-right text-sm">
                    {e.direction === 'out' ? <span className="font-semibold text-[#E60000]">{fmt(e.amount)}</span> : ''}
                  </td>
                  <td className="p-3 text-right text-sm font-semibold">{fmt(e.running_balance)}</td>
                  <td className="p-3">
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => openEdit(e)} className="p-1.5 rounded-sm hover:bg-gray-100 text-gray-600" data-testid={`edit-cashbook-${e.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => handleDelete(e.id)} className="p-1.5 rounded-sm hover:bg-red-50 text-red-600" data-testid={`delete-cashbook-${e.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              <tr className="bg-[#FFF5F5] border-t-2 border-[#E60000]">
                <td colSpan={4} className="p-3 font-bold text-sm">Endbestand {MONTH_NAMES[month - 1]} {year}</td>
                <td className="p-3 text-right font-semibold text-green-700">+ {fmt(data.total_in)}</td>
                <td className="p-3 text-right font-semibold text-[#E60000]">− {fmt(data.total_out)}</td>
                <td className="p-3 text-right font-bold text-lg">{fmt(data.ending_balance)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Kassenbuch-Eintrag bearbeiten' : 'Neuer Kassenbuch-Eintrag'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, direction: 'in' })}
                className={`p-3 border rounded-sm flex items-center justify-center gap-2 font-semibold ${
                  formData.direction === 'in' ? 'bg-green-50 border-green-600 text-green-700' : 'bg-white border-[#E2E4E9] text-gray-500'
                }`}
                data-testid="direction-in"
              >
                <ArrowDown size={18} /> Einnahme
              </button>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, direction: 'out' })}
                className={`p-3 border rounded-sm flex items-center justify-center gap-2 font-semibold ${
                  formData.direction === 'out' ? 'bg-red-50 border-[#E60000] text-[#E60000]' : 'bg-white border-[#E2E4E9] text-gray-500'
                }`}
                data-testid="direction-out"
              >
                <ArrowUp size={18} /> Ausgabe
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Datum *</Label>
                <Input type="date" value={formData.entry_date} onChange={(e) => setFormData({ ...formData, entry_date: e.target.value })} className="input-swiss" required data-testid="cashbook-date" />
              </div>
              <div>
                <Label className="overline">Betrag (€) *</Label>
                <Input type="number" min={0.01} step={0.01} value={formData.amount} onChange={(e) => setFormData({ ...formData, amount: e.target.value })} className="input-swiss" required placeholder="z. B. 150,00" data-testid="cashbook-amount" />
              </div>
            </div>

            <div>
              <Label className="overline">Kategorie *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger className="input-swiss" data-testid="cashbook-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="overline">Beschreibung *</Label>
              <Input value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} className="input-swiss" required placeholder="z. B. Tankfüllung Shell Pforzheim" data-testid="cashbook-description" />
            </div>

            <div>
              <Label className="overline">Beleg-Nr. (optional)</Label>
              <Input value={formData.document_ref} onChange={(e) => setFormData({ ...formData, document_ref: e.target.value })} className="input-swiss" placeholder="z. B. BEL-001 oder Kassenbon-Nr." />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="overline">Lieferant / Empfänger</Label>
                <Input
                  list="cashbook-supplier-datalist"
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  className="input-swiss"
                  placeholder="z. B. Shell Konstanz, ATU"
                  data-testid="cashbook-supplier-input"
                />
                <datalist id="cashbook-supplier-datalist">
                  {suppliers.map(s => <option key={s} value={s} />)}
                </datalist>
              </div>
              <div>
                <Label className="overline">Fahrzeug (optional)</Label>
                <Select
                  value={formData.vehicle_id || 'none'}
                  onValueChange={(v) => setFormData({ ...formData, vehicle_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger className="input-swiss" data-testid="cashbook-vehicle-select">
                    <SelectValue placeholder="— kein Fahrzeug —" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— kein Fahrzeug —</SelectItem>
                    {vehicles.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.brand} {v.model} ({v.license_plate})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="overline">Notizen</Label>
              <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} className="input-swiss w-full px-3 py-2" rows={2} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Abbrechen</Button>
              <Button type="submit" className="btn-primary" data-testid="save-cashbook-btn">
                {editing ? 'Speichern' : 'Eintragen'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Kassensturz / Tagesabschluss Modal */}
      <Dialog open={showClosingModal} onOpenChange={setShowClosingModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="closing-modal">
          <DialogHeader>
            <DialogTitle>Kassensturz / Tagesabschluss</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="overline">Datum</Label>
              <Input
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
                data-testid="closing-date"
              />
            </div>

            {closingLoading ? (
              <p className="text-sm text-gray-500">Lade Tagesdaten…</p>
            ) : closingData ? (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="p-3 bg-[#F7F8FA] border border-[#E2E4E9] rounded-sm">
                    <p className="text-xs text-gray-500">Anfangsbestand</p>
                    <p className="font-bold">{fmt(closingData.starting_balance)}</p>
                  </div>
                  <div className="p-3 bg-green-50 border border-green-200 rounded-sm">
                    <p className="text-xs text-gray-500">Bare Einnahmen</p>
                    <p className="font-bold text-[#009951]">{fmt(closingData.cash_in)}</p>
                  </div>
                  <div className="p-3 bg-red-50 border border-red-200 rounded-sm">
                    <p className="text-xs text-gray-500">Bare Ausgaben</p>
                    <p className="font-bold text-[#E60000]">{fmt(closingData.cash_out)}</p>
                  </div>
                  <div className="p-3 bg-[#0A0A0A]/5 border border-[#E2E4E9] rounded-sm">
                    <p className="text-xs text-gray-500">Soll-Bestand</p>
                    <p className="font-bold" data-testid="closing-expected">{fmt(closingData.expected_cash)}</p>
                  </div>
                </div>

                <p className="text-xs text-gray-500">
                  {closingData.count} Buchung(en) heute · davon werden nur bare Bewegungen für den Kassenbestand berücksichtigt.
                </p>

                <div>
                  <Label className="overline">Gezählter Bargeldbestand (Ist)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder={`Erwartet: ${closingData.expected_cash.toFixed(2)} €`}
                    value={closingCounted}
                    onChange={(e) => setClosingCounted(e.target.value)}
                    data-testid="closing-counted"
                  />
                  {closingCounted && closingDiff !== null && (
                    <div
                      className={`mt-2 p-3 rounded-sm border text-sm ${
                        Math.abs(closingDiff) < 0.01
                          ? 'bg-green-50 border-green-200 text-[#009951]'
                          : closingDiff > 0
                            ? 'bg-amber-50 border-amber-200 text-[#B25A00]'
                            : 'bg-red-50 border-red-200 text-[#E60000]'
                      }`}
                      data-testid="closing-diff"
                    >
                      {Math.abs(closingDiff) < 0.01
                        ? '✓ Bestand stimmt überein'
                        : closingDiff > 0
                          ? `Mehrbestand (Überschuss): +${fmt(closingDiff)}`
                          : `Fehlbestand (Manko): ${fmt(closingDiff)}`}
                    </div>
                  )}
                </div>

                <div>
                  <Label className="overline">Bemerkungen (optional)</Label>
                  <Input
                    placeholder="z.B. Wechselgeld neu einsortiert"
                    value={closingNotes}
                    onChange={(e) => setClosingNotes(e.target.value)}
                    data-testid="closing-notes"
                  />
                </div>
              </>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowClosingModal(false)}>Schließen</Button>
            <Button
              className="btn-primary"
              onClick={downloadClosingPdf}
              disabled={!closingData}
              data-testid="closing-pdf-btn"
            >
              <FileArrowDown size={16} className="mr-2" />
              Kassensturz-PDF herunterladen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Cashbook;
